import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useAllTrainingRequests, useTrainingCategories } from "../hooks";

const STATUS_STYLES: Record<string, string> = {
  DRAFT:     "bg-slate-100 text-slate-600",
  SUBMITTED: "bg-amber-100 text-amber-800",
  APPROVED:  "bg-emerald-100 text-emerald-700",
  REJECTED:  "bg-red-100 text-red-700",
  CANCELLED: "bg-slate-200 text-slate-500",
  COMPLETED: "bg-indigo-100 text-indigo-700",
};

function fmt(v?: string | null) {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

export default function TrainingCoursesPage() {
  const [page, setPage] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const query = useAllTrainingRequests(page, 15, statusFilter || undefined);
  const categoriesQuery = useTrainingCategories();
  const categories = categoriesQuery.data ?? [];

  if (query.isLoading) return <PageLoader label="Loading training requests…" />;
  if (query.isError) return <ErrorState title="Failed to load" message={String(query.error)} />;

  const data = query.data!;
  const items = categoryFilter
    ? data.content.filter((r) => String(r.categoryId) === categoryFilter)
    : data.content;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Training Requests</h1>
          
        </div>
        <Link
          to="/hr/training/new"
          className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" /> New Request
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          className="input w-auto min-w-[160px]"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        <select
          className="input w-auto min-w-[160px]"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
        >
          <option value="">All statuses</option>
          {["DRAFT","SUBMITTED","APPROVED","REJECTED","CANCELLED","COMPLETED"].map((s) => (
            <option key={s} value={s}>{s.replace(/_/g," ")}</option>
          ))}
        </select>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No training requests found" description="Adjust the filters or create a new request." />
      ) : (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-hidden divide-y">
            {items.map((r) => (
              <Link
                key={r.id}
                to={`/hr/training/${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{r.trainingTitle}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {r.categoryName && <span>{r.categoryName} · </span>}
                    {r.employeeName}
                    {r.providerName && ` · ${r.providerName}`}
                    {fmt(r.trainingStartDate) && ` · ${fmt(r.trainingStartDate)}`}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status] ?? "bg-slate-100 text-slate-600"}`}>
                  {r.status.replace(/_/g," ")}
                </span>
              </Link>
            ))}
          </div>
          <DataPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            size={data.size}
            onPageChange={setPage}
            onSizeChange={() => setPage(0)}
          />
        </>
      )}
    </div>
  );
}
